// B1385: Functions — the stored functions, who may call them, and as whom
// they run. Every function is the server's answer to `SHOW FUNCTIONS`, and
// every INVOKE rule is a `kind = function` row of `SHOW GRANTS` (B1376).
// Every change is a CREATE [OR REPLACE] / DROP FUNCTION or a GRANT / REVOKE
// INVOKE of web/docs/sql.md § Functions and tickers, sent on the shared WS.
// After each change the page reloads; it never patches its lists, because the
// server's answer is the truth.
//
// `SHOW FUNCTIONS` gives the typed signature (B2172) but no body. A legacy
// function has no types; the server refuses its CALL, and the page marks it.
// The Edit row loads the body with `SHOW CREATE FUNCTION` (B1386) and sends
// CREATE OR REPLACE FUNCTION. The server lets a superuser or the run-as user
// of the function read the body; every other session sees the server's refusal in the row.
//
// `SHOW GRANTS`, `SHOW ROLES` and `SHOW USERS` are superuser-only. A non-root
// session sees the server's `permission denied`, never a fake empty list.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { I } from '../components/icons.jsx';
import * as fns from '../data/functions-store.js';
import * as users from '../data/users-store.js';

function errText(e) { return e?.message || String(e); }

// The server's refusal of a CALL to a legacy (untyped) function (B2172).
function legacyText(name) {
  return `function ${name} has no parameter types: replace it with CREATE OR REPLACE FUNCTION`;
}

// Every listing the page shows, loaded together. Each listing keeps its own
// error: a refused `SHOW GRANTS` must not hide the functions, and the reverse.
function useFunctionsData() {
  const [state, setState] = useState({
    functions: null, grants: null, roles: null, users: null, errors: {}, loading: true,
  });
  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const [f, g, r, u] = await Promise.allSettled([
      fns.listFunctions(), fns.listFunctionGrants(), users.listRoles(), users.listUsers(),
    ]);
    const val = (p) => (p.status === 'fulfilled' ? p.value : null);
    const err = (p) => (p.status === 'rejected' ? errText(p.reason) : null);
    setState({
      functions: val(f), grants: val(g), roles: val(r), users: val(u),
      errors: { functions: err(f), grants: err(g), roles: err(r), users: err(u) },
      loading: false,
    });
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

// Run one server action, surface its error next to the control, then reload.
function useAction(reload) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = useCallback(async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); await reload(); return true; }
    catch (e) { setError(errText(e)); return false; }
    finally { setBusy(false); }
  }, [reload]);
  return { busy, error, setError, run };
}

function WhoText({ row }) {
  const t = row.target;
  if (t.kind === 'role') return <span>role <span className="mono">{t.name}</span></span>;
  if (t.kind === 'anyone') return <span>anyone</span>;
  if (t.kind === 'authenticated') return <span>every logged-in user</span>;
  return <span className="mono">{row.grant}</span>;
}

// ── one INVOKE rule: Revoke with a confirmation step ────────────────────
function GrantItem({ row, reload }) {
  const [confirm, setConfirm] = useState(false);
  const { busy, error, run } = useAction(reload);
  const rev = fns.revokeInvokeFor(row);
  return (
    <div className="fn-grant">
      <WhoText row={row} />
      {rev.sql ? (
        confirm ? (
          <>
            <button className="btn btn-sm users-danger" type="button" disabled={busy} title={rev.sql}
              onClick={async () => { if (await run(() => fns.runSql([rev.sql]))) setConfirm(false); }}>Revoke</button>
            <button className="btn btn-ghost btn-sm" type="button" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>
          </>
        ) : (
          <button className="btn btn-ghost btn-sm" type="button" disabled={busy} title={rev.sql} onClick={() => setConfirm(true)}>Revoke…</button>
        )
      ) : (
        <span className="users-muted">Read-only: {rev.reason}</span>
      )}
      {confirm && rev.sql && <div className="access-sql">{rev.sql}</div>}
      {error && <div className="users-error">{error}</div>}
    </div>
  );
}

// ── the Grant INVOKE form of one function ───────────────────────────────
function GrantForm({ fn, roles, rolesError, reload }) {
  const [kind, setKind] = useState('authenticated');
  const [role, setRole] = useState('');
  const { busy, error, run } = useAction(reload);

  let sql = null;
  let composeErr = null;
  try { sql = fns.grantInvokeSql(fn, kind === 'role' ? { kind, name: role } : { kind }); } catch (e) { composeErr = errText(e); }

  async function submit(e) {
    e.preventDefault();
    if (!sql) return;
    if (await run(() => fns.runSql([sql]))) setRole('');
  }

  return (
    <form className="access-add" onSubmit={submit}>
      <div className="users-form">
        <span className="users-label">Grant INVOKE to</span>
        <select value={kind} onChange={(e) => setKind(e.target.value)} disabled={busy}>
          <option value="authenticated">AUTHENTICATED</option>
          <option value="anyone">ANYONE (the guest too)</option>
          <option value="role">ROLE</option>
        </select>
        {kind === 'role' && (
          <select value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
            <option value="">{(roles ?? []).length ? 'pick a role…' : 'no roles'}</option>
            {(roles ?? []).map((r) => <option key={r.role} value={r.role}>{r.role}</option>)}
          </select>
        )}
        <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !sql}><I.Plus size={12} /> Grant</button>
      </div>
      {kind === 'role' && rolesError && <div className="users-error">{rolesError}</div>}
      {sql && <div className="access-sql">{sql}</div>}
      {kind === 'role' && composeErr && <div className="users-muted">{composeErr}</div>}
      {error && <div className="users-error">{error}</div>}
    </form>
  );
}

// ── the typed signature editor (B2172) ──────────────────────────────────
// A kd function has a typed signature: every parameter and RETURNS have a
// type (web/docs/sql.md § Functions and tickers). A row of the editor is
// {name, kind, size, null}: `kind` is a type of `fns.PARAM_TYPES`, and
// `size` is the n of VECTOR(n); an empty `size` is a plain VECTOR (a list of
// any size). NOT NULL is the default. An empty `kind` is
// no type yet; `createSql` then refuses the row with the server's words.
const NEW_ROW = { name: '', kind: 'INT', size: '', null: false };

function rowOf(p) {
  const v = /^VECTOR\((\d+)\)$/.exec(p.type ?? '');
  return { name: p.name, kind: v ? 'VECTOR' : (p.type ?? ''), size: v ? v[1] : '', null: p.null === true };
}
function typeOfRow(r) { return r.kind === 'VECTOR' && r.size !== '' ? `VECTOR(${r.size})` : r.kind; }
function typedOfRow(r) { return { name: r.name.trim(), type: r.kind === '' ? null : typeOfRow(r), null: r.null }; }

// The returns state: {kind, size, null, cols}. `kind` = 'TABLE' uses `cols`.
const NEW_RETURNS = { kind: 'INT', size: '', null: false, cols: [{ ...NEW_ROW }] };

function returnsStateOf(text) {
  if (text == null || text === '') return { ...NEW_RETURNS, kind: '' };
  try {
    const r = fns.parseReturns(text);
    if (r.table) return { ...NEW_RETURNS, kind: 'TABLE', cols: r.table.map(rowOf) };
    return { ...NEW_RETURNS, ...rowOf({ name: '', type: r.type, null: r.null }), cols: NEW_RETURNS.cols };
  } catch {
    return { ...NEW_RETURNS, kind: '' };
  }
}
function returnsOfState(s) {
  if (s.kind === '') return null;
  if (s.kind === 'TABLE') return { table: s.cols.map(typedOfRow) };
  return { type: typeOfRow(s), null: s.null };
}

// The editor state of a `SHOW FUNCTIONS` row. A legacy function (typed =
// false, `param_types` []) has names but no types: each row then has no
// type, and the admin picks one.
function signatureStateOf(f) {
  const params = f?.paramTypes && f.typed !== false
    ? f.paramTypes.map(rowOf)
    : (f?.params ?? []).map((name) => ({ ...NEW_ROW, name, kind: '' }));
  return { params, returns: returnsStateOf(f?.returns) };
}

function TypeSelect({ row, onChange, busy }) {
  return (
    <>
      <select value={row.kind} onChange={(e) => onChange({ ...row, kind: e.target.value })} disabled={busy}>
        {row.kind === '' && <option value="">pick a type…</option>}
        {fns.PARAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      {row.kind === 'VECTOR' && (
        <input className="fn-size" placeholder="any n" value={row.size} inputMode="numeric"
          onChange={(e) => onChange({ ...row, size: e.target.value.trim() })} disabled={busy} />
      )}
      <label className={'users-check' + (row.null ? ' on' : '')} title="NOT NULL is the default">
        <input type="checkbox" checked={row.null} onChange={() => onChange({ ...row, null: !row.null })} disabled={busy} />
        NULL
      </label>
    </>
  );
}

// A list of typed rows: the parameters, or the columns of RETURNS TABLE.
function TypedRows({ rows, onChange, busy, placeholder, addText }) {
  const set = (i, r) => onChange(rows.map((x, j) => (j === i ? r : x)));
  return (
    <div className="fn-rows">
      {rows.map((r, i) => (
        <div className="users-form" key={i}>
          <input className="fn-name" placeholder={placeholder} value={r.name}
            onChange={(e) => set(i, { ...r, name: e.target.value })} spellCheck={false} disabled={busy} />
          <TypeSelect row={r} onChange={(x) => set(i, x)} busy={busy} />
          <button className="btn btn-ghost btn-sm" type="button" title="Remove" disabled={busy}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}><I.Close size={12} /></button>
        </div>
      ))}
      <div className="users-form">
        <button className="btn btn-ghost btn-sm" type="button" disabled={busy}
          onClick={() => onChange([...rows, { ...NEW_ROW }])}><I.Plus size={12} /> {addText}</button>
      </div>
    </div>
  );
}

// The parameters and RETURNS of one function. `value` is
// {params, returns}; `signatureArgs(value)` gives the arguments of
// `fns.createSql`.
function SignatureEditor({ value, onChange, busy }) {
  const ret = value.returns;
  const setRet = (r) => onChange({ ...value, returns: r });
  return (
    <>
      <div className="users-form">
        <span className="users-label">Parameters</span>
        {value.params.length === 0 && <span className="users-muted">none</span>}
      </div>
      <TypedRows rows={value.params} onChange={(params) => onChange({ ...value, params })} busy={busy}
        placeholder="x" addText="Parameter" />
      <div className="users-form">
        <span className="users-label">Returns</span>
        <select value={ret.kind} onChange={(e) => setRet({ ...ret, kind: e.target.value })} disabled={busy}>
          {ret.kind === '' && <option value="">pick a type…</option>}
          {fns.PARAM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          <option value="TABLE">TABLE (…)</option>
        </select>
        {ret.kind === 'VECTOR' && (
          <input className="fn-size" placeholder="any n" value={ret.size} inputMode="numeric"
            onChange={(e) => setRet({ ...ret, size: e.target.value.trim() })} disabled={busy} />
        )}
        {ret.kind !== 'TABLE' && (
          <label className={'users-check' + (ret.null ? ' on' : '')} title="NOT NULL is the default">
            <input type="checkbox" checked={ret.null} onChange={() => setRet({ ...ret, null: !ret.null })} disabled={busy} />
            NULL
          </label>
        )}
      </div>
      {ret.kind === 'TABLE' && (
        <TypedRows rows={ret.cols} onChange={(cols) => setRet({ ...ret, cols })} busy={busy}
          placeholder="column" addText="Column" />
      )}
    </>
  );
}

function signatureArgs(value) {
  return { params: value.params.map(typedOfRow), returns: returnsOfState(value.returns) };
}

// ── the Edit form of one function ───────────────────────────────────────
// It loads the body with `SHOW CREATE FUNCTION`, then sends CREATE OR
// REPLACE FUNCTION with the edited signature, body and run-as user. The
// signature starts from the `param_types` and `returns` of `SHOW FUNCTIONS`.
// An empty run-as user is no RUN AS clause: the body then runs as the caller.
function EditForm({ f, users: userList, reload, onDone }) {
  const fn = f.name;
  const [loaded, setLoaded] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [sig, setSig] = useState(() => signatureStateOf(f));
  const [body, setBody] = useState('');
  const [runAs, setRunAs] = useState('');
  const { busy, error, run } = useAction(reload);

  useEffect(() => {
    let live = true;
    setLoaded(null); setLoadErr(null);
    fns.loadFunctionSource(fn).then(
      (d) => {
        if (!live) return;
        setLoaded(d);
        setBody(d.source);
        setRunAs(d.runAs ?? '');
      },
      (e) => { if (live) setLoadErr(errText(e)); },
    );
    return () => { live = false; };
  }, [fn]);

  if (loadErr) return <div className="users-error">{loadErr}</div>;
  if (!loaded) return <div className="users-muted">Loading the definition…</div>;

  let sql = null;
  let composeErr = null;
  try { sql = fns.createSql({ name: fn, ...signatureArgs(sig), body, runAs: runAs.trim(), orReplace: true }); } catch (e) { composeErr = errText(e); }

  async function submit(e) {
    e.preventDefault();
    if (!sql) return;
    if (await run(() => fns.runSql([sql]))) onDone();
  }

  return (
    <form className="access-add" onSubmit={submit}>
      {f.typed === false && <div className="users-error">{legacyText(fn)}</div>}
      <SignatureEditor value={sig} onChange={setSig} busy={busy} />
      <div className="users-form">
        <span className="users-label">Body (kd)</span>
      </div>
      <textarea className="fn-body" rows={8} value={body}
        onChange={(e) => setBody(e.target.value)} spellCheck={false} disabled={busy} />
      <div className="users-form">
        <span className="users-label">Run as</span>
        <input list="fn-users-edit" placeholder="caller (empty)" value={runAs} onChange={(e) => setRunAs(e.target.value)} spellCheck={false} disabled={busy} />
        <datalist id="fn-users-edit">
          {(userList ?? []).map((u) => <option key={u.user} value={u.user} />)}
        </datalist>
      </div>
      {sql && <div className="access-sql">{sql}</div>}
      {composeErr && <div className="users-muted">{composeErr}</div>}
      <div className="users-form">
        <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !sql}>Replace</button>
        <button className="btn btn-ghost btn-sm" type="button" disabled={busy} onClick={onDone}>Cancel</button>
        {error && <span className="users-error">{error}</span>}
      </div>
    </form>
  );
}

// The signature cell of one function. A legacy (untyped) function shows its
// names and the server's refusal: no CALL runs until Edit replaces it. An
// older server sends no types: the cell shows the names only.
function Signature({ f }) {
  const sig = fns.signatureText(f);
  if (sig) return <span className="mono">{sig}</span>;
  const names = f.params.length ? <span className="mono">({f.params.join(', ')})</span> : <span className="users-muted">none</span>;
  if (f.typed !== false) return names;
  return (
    <>
      {names}
      <div className="users-error" title="Edit… writes the types">{legacyText(f.name)}</div>
    </>
  );
}

// ── one function: its row, and its editor row when open ─────────────────
// `mode` is 'edit', 'grant' or null (closed).
function FunctionRow({ f, grants, grantsError, data, mode, onOpen }) {
  const [confirm, setConfirm] = useState(false);
  const { busy, error, run } = useAction(data.reload);
  let drop = null;
  let dropErr = null;
  try { drop = fns.dropSql(f.name); } catch (e) { dropErr = errText(e); }

  return (
    <>
      <tr className={mode ? 'users-row-open' : ''}>
        <td className="fg1 mono">{f.name}</td>
        <td><Signature f={f} /></td>
        <td>{f.language}</td>
        <td>{f.runAs ? <span className="mono">{f.runAs}</span> : <span className="users-muted">caller</span>}</td>
        <td>
          {grantsError
            ? <span className="users-muted">—</span>
            : grants == null
              ? <span className="users-muted">…</span>
              : grants.length === 0
                ? <span className="users-muted">root only</span>
                : grants.map((g) => <GrantItem key={g.grant} row={g} reload={data.reload} />)}
        </td>
        <td className="access-actions">
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => onOpen(mode === 'edit' ? null : 'edit')}>{mode === 'edit' ? 'Close' : 'Edit…'}</button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => onOpen(mode === 'grant' ? null : 'grant')}>{mode === 'grant' ? 'Close' : 'Grant…'}</button>
          {drop && (confirm ? (
            <>
              <button className="btn btn-sm users-danger" type="button" disabled={busy} title={drop}
                onClick={async () => { if (await run(() => fns.runSql([drop]))) setConfirm(false); }}>Drop</button>
              <button className="btn btn-ghost btn-sm" type="button" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>
            </>
          ) : (
            <button className="btn btn-ghost btn-sm" type="button" disabled={busy} title={drop} onClick={() => setConfirm(true)}>Drop…</button>
          ))}
          {confirm && drop && <div className="access-sql">{drop}</div>}
          {dropErr && <div className="users-error">{dropErr}</div>}
          {error && <div className="users-error">{error}</div>}
        </td>
      </tr>
      {mode && (
        <tr className="users-editor-row">
          <td colSpan={6}>
            <div className="users-editor">
              {mode === 'edit'
                ? <EditForm f={f} users={data.users} reload={data.reload} onDone={() => onOpen(null)} />
                : <GrantForm fn={f.name} roles={data.roles} rolesError={data.errors.roles} reload={data.reload} />}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── the Create form ─────────────────────────────────────────────────────
function CreateForm({ data }) {
  const [name, setName] = useState('');
  const [sig, setSig] = useState(() => signatureStateOf({ paramTypes: [], returns: 'INT' }));
  const [body, setBody] = useState('');
  const [runAs, setRunAs] = useState('');
  const [orReplace, setOrReplace] = useState(false);
  const { busy, error, run } = useAction(data.reload);

  let sql = null;
  let composeErr = null;
  if (name || body) {
    try { sql = fns.createSql({ name, ...signatureArgs(sig), body, runAs: runAs.trim(), orReplace }); } catch (e) { composeErr = errText(e); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!sql) return;
    if (await run(() => fns.runSql([sql]))) {
      setName(''); setSig(signatureStateOf({ paramTypes: [], returns: 'INT' })); setBody(''); setRunAs(''); setOrReplace(false);
    }
  }

  return (
    <form className="access-add" onSubmit={submit}>
      <div className="users-form">
        <span className="users-label">Name</span>
        <input placeholder="add_one" value={name} onChange={(e) => setName(e.target.value)} spellCheck={false} disabled={busy} />
      </div>
      <SignatureEditor value={sig} onChange={setSig} busy={busy} />
      <div className="users-form">
        <span className="users-label">Body (kd)</span>
      </div>
      <textarea className="fn-body" rows={8} placeholder="return x + 1;" value={body}
        onChange={(e) => setBody(e.target.value)} spellCheck={false} disabled={busy} />
      <div className="users-form">
        <span className="users-label">Run as</span>
        <input list="fn-users" placeholder="caller (empty)" value={runAs} onChange={(e) => setRunAs(e.target.value)} spellCheck={false} disabled={busy} />
        <datalist id="fn-users">
          {(data.users ?? []).map((u) => <option key={u.user} value={u.user} />)}
        </datalist>
      </div>
      <div className="users-form">
        <label className={'users-check' + (orReplace ? ' on' : '')}>
          <input type="checkbox" checked={orReplace} onChange={() => setOrReplace(!orReplace)} disabled={busy} />
          replace if it exists
        </label>
      </div>
      {sql && <div className="access-sql">{sql}</div>}
      {composeErr && <div className="users-muted">{composeErr}</div>}
      <div className="users-form">
        <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !sql}><I.Plus size={12} /> {orReplace ? 'Create or replace' : 'Create'}</button>
        {error && <span className="users-error">{error}</span>}
      </div>
    </form>
  );
}

// ── the page ─────────────────────────────────────────────────────────────
export function FunctionsPage() {
  const data = useFunctionsData();
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(null); // { name, mode } or null

  const byFn = useMemo(() => {
    const m = new Map();
    for (const g of data.grants ?? []) {
      if (!m.has(g.fn)) m.set(g.fn, []);
      m.get(g.fn).push(g);
    }
    return m;
  }, [data.grants]);

  const list = data.functions ?? [];
  const q = filter.trim().toLowerCase();
  const shown = q ? list.filter((f) => f.name.toLowerCase().includes(q)) : list;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="crumbs">Admin · Functions</div>
          <h1>Functions</h1>
        </div>
        <div className="actions">
          <button className="btn btn-sm btn-ghost" type="button" onClick={data.reload} disabled={data.loading} title="Reload"><I.Refresh size={12} /> Refresh</button>
        </div>
      </div>
      <div className="users-grid">
        <div className="k-card">
          <div className="k-card-head">
            <span className="title">{data.functions == null ? 'Functions' : `${list.length} function${list.length === 1 ? '' : 's'}`}</span>
            <div className="actions">
              <div className="users-filter">
                <I.Search size={12} />
                <input placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              </div>
            </div>
          </div>
          {data.errors.functions && <div className="users-error access-note">{data.errors.functions}</div>}
          {data.errors.grants && <div className="users-error access-note">INVOKE rules: {data.errors.grants}</div>}
          <table className="k-table access-table">
            <thead><tr><th>Name</th><th>Signature</th><th>Language</th><th>Runs as</th><th>INVOKE</th><th></th></tr></thead>
            <tbody>
              {data.functions == null && !data.errors.functions && (
                <tr><td colSpan={6} className="users-muted">Loading…</td></tr>
              )}
              {data.functions != null && shown.length === 0 && (
                <tr><td colSpan={6} className="users-muted">{q ? 'Nothing matches the filter.' : 'No functions.'}</td></tr>
              )}
              {shown.map((f) => (
                <FunctionRow key={f.name} f={f} data={data}
                  grants={data.grants == null ? null : (byFn.get(f.name) ?? [])} grantsError={data.errors.grants}
                  mode={open?.name === f.name ? open.mode : null}
                  onOpen={(mode) => setOpen(mode ? { name: f.name, mode } : null)} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="k-card">
          <div className="k-card-head"><span className="title">New function</span></div>
          <div className="access-note">
            <CreateForm data={data} />
          </div>
        </div>
      </div>
    </div>
  );
}
